<template>
  <div>
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="customerDetailForm">
        <b-form
          :key="values.type"
          ref="customerDetailFormElement"
          class=""
          @submit.prevent="submit"
        >
          <customer-account-form
            v-if="isAccountTab"
            :type="type"
            :values="values"
            :customer-types="customerTypes"
            :customer-payment-type-options="customerPaymentTypeOptions"
            :package-options="packageOptions"
            :customer-status-types="customerStatusTypes"
          />

          <customer-finance-form
            v-if="isFinanceTab"
            :type="type"
            :values="values"
            :countries="countries"
            :cities="cities"
            :districts="districts"
            :company-types="companyTypes"
            :customer-postal-code-options="customerPostalCodeOptions"
            :on-country-change="onCountryChange"
            :on-city-change="onCityChange"
            :handle-customer-postal-code-search="handleCustomerPostalCodeSearch"
            @update-current-account-code="$emit('update-current-account-code', $event)"
          />

          <b-row>
            <b-col v-if="type === 'new'" cols="12">
              <b-button
                variant="primary"
                class="mt-1 d-flex align-items-center"
                type="submit"
                tabindex="11"
              >
                {{ submitting ? t('Please Wait') : t('Create Customer') }}
              </b-button>
            </b-col>
            <b-col v-if="type === 'edit' && isAccountTab" cols="12">
              <b-button
                variant="primary"
                class="mt-1 d-flex align-items-center"
                type="submit"
                tabindex="11"
              >
                {{ submitting ? t('Please Wait') : t('Edit') }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>
<script>
import { BCol, BForm, BRow, BButton, BOverlay } from 'bootstrap-vue';
import { ValidationObserver } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';
import CustomerAccountForm from './components/CustomerAccountForm.vue';
import CustomerFinanceForm from './components/CustomerFinanceForm.vue';

export default {
  components: {
    BOverlay,
    BButton,
    BRow,
    BCol,
    BForm,
    CustomerAccountForm,
    CustomerFinanceForm,

    ValidationObserver
  },
  mixins: [helper],
  props: {
    type: {
      type: String,
      default: 'new'
    },
    submitting: {
      type: Boolean,
      default: false
    },
    values: {
      type: Object,
      default() {
        return {
          name: '',
          authorizedName: '',
          authorizedNo: '',
          phone: '',
          fixed_phone: '',
          tax_office: '',
          tax_no: '',
          mersis_number: '',
          trade_registry_number: '',
          address: '',
          country: '',
          city: '',
          postalCode: '',
          companyType: '',
          state: '',
          email: '',
          iban: '',
          company_fixed_phone: '',
          current_account_code: '',
          paymentType: 0,
          package_id: '',
          status: 1,
          type: 2
        };
      }
    },
    activeTab: {
      type: String,
      default: 'account'
    }
  },
  data() {
    return {
      paymentTypeOptions: [],
      statusOptions: [],
      rolesOptions: [],
      packageOptions: [],
      customerStatusTypes: [],
      customerTypes: [],
      companyTypes: [],
      countries: [],
      cities: [],
      districts: [],
      selectedCountry: null,
      selectedCity: null,
      selectedDistrict: null,
      minimumPostalCodeLength: 3,
      filteredPostalCodes: []
    };
  },

  computed: {
    isAccountTab() {
      return this.activeTab === 'account';
    },
    isFinanceTab() {
      return this.activeTab === 'finance' && this.values.type === 2;
    },
    customerPaymentTypeOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.paymentTypeOptions && this.paymentTypeOptions.length > 0) {
        options.push(
          ...this.paymentTypeOptions.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    },
    customerPostalCodeOptions() {
      return this.filteredPostalCodes.map(code => ({
        value: code.zipcode,
        combinedLabel: `${code.zipcode} - ${code.place}`,
        state: code.state,
        city: code.city
      }));
    }
  },

  mounted() {
    this.statusOptions = [
      { value: 1, text: this.$t('Active') },
      { value: 0, text: this.$t('Passive') }
    ];
  },

  async created() {
    try {
      await Promise.all([
        useService.getCustomerStatusTypes().then(response => {
          this.customerStatusTypes =
            response.data.map(i => ({
              text: i.info,
              value: i.value
            })) || [];
        }),

        useService.getCustomerTypes().then(response => {
          this.customerTypes =
            response.data.map(i => ({
              text: i.info,
              value: i.value
            })) || [];
        }),

        this.getCountries(),

        this.getCustomerPaymentType().then(response => {
          this.paymentTypeOptions = response;
        }),

        this.getPackages().then(response => {
          this.packageOptions = response.map(x => ({
            text: x.title,
            value: x.id
          }));
        }),

        this.getCompanyTypes()
      ]);
    } catch (error) {
      this.showErrorMessage(this.$t('Failed to load data'));
    }
  },
  methods: {
    submit() {
      this.$refs.customerDetailForm.validate().then(success => {
        if (success) {
          const basePayload = {
            name: this.values.name,
            authorized_name: this.values.authorizedName,
            authorized_no: this.values.authorizedNo,
            phone: this.values.phone,
            email: this.values.email,
            mersis_number: this.values.mersis_number,
            trade_registry_number: this.values.trade_registry_number,
            payment_type: parseInt(this.values.paymentType),
            status: this.values.status,
            package_id: this.values.package_id,
            type: this.values.type
          };

          let payload = { ...basePayload };

          if (this.type === 'new') {
            payload = {
              ...payload,
              address: this.values.address,
              country: this.values.country?.value || '',
              city: this.values.city?.value || '',
              state: this.values.state?.value || '',
              postal_code:
                typeof this.values.postalCode === 'object'
                  ? this.values.postalCode.value
                  : this.values.postalCode,
              company_type: parseInt(this.values.companyType),
              tax_office: this.values.tax_office,
              tax_no: this.values.tax_no
            };
          }

          this.$emit('submit', payload);
        }
      });
    },
    resetForm() {
      this.$refs.customerDetailFormElement.reset();
    },

    async getCountries() {
      try {
        const response = await useService.getCountryList();
        this.countries = response.data.map(country => ({
          text: country.name,
          value: country.id,
          iso2: country.iso2
        }));
      } catch (error) {
        this.showErrorMessage(this.$t('Failed to load countries'));
      }
    },

    async getCities(countryId) {
      try {
        const response = await useService.getStateList(countryId);
        this.cities = response.data.map(city => ({
          text: city.name,
          value: city.id
        }));
      } catch (error) {
        this.showErrorMessage(this.$t('Failed to load data'));
      }
    },

    async getDistricts(cityId) {
      try {
        const response = await useService.getCityList(this.values.country.value, cityId);
        this.districts = response.data.map(district => ({
          text: district.name,
          value: district.id
        }));
      } catch (error) {
        this.showErrorMessage(this.$t('Failed to load data'));
      }
    },

    async getCompanyTypes() {
      try {
        const response = await useService.constantService.getCompanyType();
        this.companyTypes = response.data.map(type => ({
          text: type.info,
          value: type.value
        }));
      } catch (error) {
        this.showErrorMessage(this.$t('Failed to load data'));
      }
    },

    onCountryChange(country) {
      if (country && country.value) {
        this.getCities(country.value);
        this.values.city = '';
        this.values.state = '';
        this.values.postalCode = '';
        this.filteredPostalCodes = [];
        this.cities = [];
        this.districts = [];
      }
    },

    onCityChange(city) {
      if (city && city.value) {
        this.getDistricts(city.value);
        this.values.state = '';
        this.districts = [];
      }
    },

    handleCustomerPostalCodeSearch(postalCode) {
      if (!this.values.country || !this.values.country.iso2) return;
      if (postalCode && postalCode.length >= this.minimumPostalCodeLength) {
        const data = {
          country_code: this.values.country.iso2,
          zipcode: postalCode
        };

        useService.autoPostalCodes(data).then(response => {
          const list = response.data || [];
          const filtered = list.filter(code => code.zipcode && code.zipcode.includes(postalCode));
          this.filteredPostalCodes = filtered.map(code => ({
            place: code.place,
            zipcode: code.zipcode,
            state: code.state,
            city: code.province || code.city
          }));
        });
      }
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
